import { FetchError } from 'ofetch'
import { asRecord, asString } from '~/utils/narrow'

export interface StampinupError {
  code: string
  message: string
}

// Parsed error body the backend returns on API errors (read in catch handlers).
export interface ApiErrorData {
  message?: string
  code?: string
  statusReason?: string
  errors?: StampinupError[]
  apiError?: { errors?: StampinupError[] }
}

export interface ApiErrorResponse {
  errors: StampinupError[]
}

export interface ApiFailure {
  code?: string
  // What code routing matches against, always present: the listed errors when the failure
  // carried a list, else one entry synthesized from its flat code and its flat or client text.
  routingErrors: StampinupError[]
  // The error list exactly as the failure carried it. Undefined distinguishes a missing
  // list from an explicitly empty one, so read this to ask whether a list was sent.
  listedErrors?: StampinupError[]
  backendMessages: string[]
  displayMessages: string[]
}

// Only the envelope is narrowed here; endpoint-specific payloads remain the caller's contract.
export const getApiErrorData = <T extends object = ApiErrorData>(error: unknown): T | undefined =>
  asRecord(asRecord(error)?.data) as T | undefined

/** Reads only the error's finite numeric statusCode, never backend body metadata. */
export const getErrorStatus = (error: unknown): number | undefined => {
  const status = asRecord(error)?.statusCode
  return typeof status === 'number' && Number.isFinite(status) ? status : undefined
}

// A domain `data` field beside an error list is part of the payload, not another envelope.
// Direct errors take precedence, including an explicitly empty array.
const parseApiErrors = (data: Record<string, unknown> | undefined): StampinupError[] | undefined => {
  const nested = asRecord(data?.apiError)
  const raw = Array.isArray(data?.errors) ? data.errors : nested?.errors
  if (!Array.isArray(raw)) {
    return undefined
  }
  const errors: StampinupError[] = []
  for (const value of raw) {
    const entry = asRecord(value)
    const code = asString(entry?.code)
    const message = asString(entry?.message)
    if (code !== undefined || message !== undefined) {
      errors.push({ code: code ?? '', message: message ?? '' })
    }
  }
  return errors
}

/** Shared display policy: omit blank text, preserve literal pipes and original readable wording. */
export const readableMessages = (values: readonly string[], fallback?: string): string[] => {
  const messages = values.filter(message => message.trim().length > 0)
  return messages.length > 0 ? messages : fallback?.trim() ? [fallback] : []
}

interface FailureEnvelope {
  // Only a response body's text counts as backend text.
  hasBody: boolean
  // The flat `message`, else `statusReason` when that one is blank or missing.
  flatMessage?: string
  clientMessage?: string
  listedErrors?: StampinupError[]
  code?: string
}

// ofetch words a FetchError's message as `[METHOD] "url": <status> ...`. A failed
// `useAsyncData` fetch reaches the page as Nuxt's own error instead: H3 copies that message
// and the response body onto it, and the FetchError survives only as `cause` -- not even
// that for a network failure, or once the error has crossed the SSR payload. The text is
// the one mark of a transport error that survives all three, so the wrapped form is
// recognized by it.
const REQUEST_LINE = /^\[[A-Za-z]+\] "/

const isTransportError = (error: unknown): boolean =>
  error instanceof FetchError || REQUEST_LINE.test(asString(asRecord(error)?.message) ?? '')

// A registered symbol, so the mark holds across duplicated copies of this module.
const USER_FACING = Symbol.for('stampinup.error.userFacing')

const isUserFacing = (error: Error): boolean => Reflect.get(error, USER_FACING) === true

/**
 * Vouches that an Error's own message may be shown to the user: a localized message raised
 * on purpose, or a rejection Recurly hands its error callback, whose text is shown until
 * there is approved copy for its codes. Returns the value it was given. Anything that is not
 * an Error passes through untouched, since a plain value is already read as given.
 */
export const userFacing = <T>(error: T): T => {
  if (error instanceof Error) {
    Object.defineProperty(error, USER_FACING, { value: true, configurable: true })
  }
  return error
}

// Whether a failure's own fields are text for the user. An Error's are not: its `message`
// is written for a developer -- a transport error's is a technical request line, a runtime
// error's is engine wording, a guard's is whatever its author typed -- so an Error lends its
// own fields only when it was raised as user-facing. Any other value (a string, a list of
// strings, an SDK's rejection object) was passed to be read.
const lendsOwnFields = (error: unknown): boolean =>
  error instanceof Error ? isUserFacing(error) : !isTransportError(error)

const resolveFailureEnvelope = (error: unknown): FailureEnvelope => {
  const body = asRecord(asRecord(error)?.data)
  // An Error that is not user-facing contributes its response body and nothing else. This
  // is the one place that rule is applied.
  const own = lendsOwnFields(error) ? asRecord(error) : undefined
  const data = body ?? own
  const message = asString(data?.message)
  return {
    hasBody: body !== undefined,
    flatMessage: message?.trim() ? message : asString(data?.statusReason),
    clientMessage: asString(own?.message) ?? asString(error),
    listedErrors: parseApiErrors(data),
    code: asString(data?.code)
  }
}

// Backend text is the response body's alone: its listed messages, else its flat text.
const selectBackendMessages = ({ hasBody, listedErrors, flatMessage }: FailureEnvelope): string[] => {
  if (!hasBody) {
    return []
  }
  const listed = readableMessages(listedErrors?.map(entry => entry.message) ?? [])
  return listed.length > 0 || flatMessage === undefined ? listed : readableMessages([flatMessage])
}

const selectRoutingErrors = (error: unknown, envelope: FailureEnvelope): StampinupError[] => {
  const { listedErrors, code, flatMessage, clientMessage } = envelope
  if (Array.isArray(error)) {
    return error
      .filter((value): value is string => typeof value === 'string')
      .map(message => ({ code: '', message }))
  }
  if (listedErrors !== undefined) {
    return listedErrors
  }
  // A code with no text still routes, as a code-only entry of a list does.
  const message = flatMessage ?? clientMessage
  return code === undefined && message === undefined ? [] : [{ code: code ?? '', message: message ?? '' }]
}

/** Normalize an envelope once when a consumer needs both business routing and display text. */
export const readApiFailure = (error: unknown): ApiFailure => {
  const envelope = resolveFailureEnvelope(error)
  const backendMessages = selectBackendMessages(envelope)
  const routingErrors = selectRoutingErrors(error, envelope)
  return {
    code: envelope.code,
    routingErrors,
    listedErrors: envelope.listedErrors,
    backendMessages,
    // Display text is the backend's when it has any, else whatever the routing errors carry.
    displayMessages: backendMessages.length > 0
      ? backendMessages
      : readableMessages(routingErrors.map(entry => entry.message))
  }
}

export const hasApiErrorCode = (error: unknown, code: string): boolean => {
  const failure = readApiFailure(error)
  return failure.code === code || failure.routingErrors.some(entry => entry.code === code)
}

// The four message accessors take (error, fallback): a backend-only or a display policy,
// as a list or as its first entry. The fallback is optional except on
// `getBackendErrorMessage`, whose single-message surfaces must always show something.
// User-facing callers reach them through `useErrorText`, which binds the localized
// generic fallback.

/** Backend-only list or flat text; never expose client or transport messages. */
export const getBackendErrorMessages = (error: unknown, fallback?: string): string[] =>
  readableMessages(readApiFailure(error).backendMessages, fallback)

/** Backend-only single-message surfaces must supply their localized fallback. */
export const getBackendErrorMessage = (error: unknown, fallback: string): string =>
  readApiFailure(error).backendMessages[0] ?? fallback

/** First display message: backend text first, else a user-facing Error's or a plain value's own text. */
export function getDisplayMessage(error: unknown, fallback: string): string
export function getDisplayMessage(error: unknown, fallback?: string): string | undefined
export function getDisplayMessage(error: unknown, fallback?: string): string | undefined {
  return readApiFailure(error).displayMessages[0] ?? fallback
}

/** Display list: backend text first, else a user-facing Error's or a plain value's own text. */
export const getDisplayMessages = (error: unknown, fallback?: string): string[] =>
  readableMessages(readApiFailure(error).displayMessages, fallback)

/**
 * Preserves an Error; any other value becomes one carrying its display text, marked
 * user-facing so whoever it is handed to can show it. A caller that shows the message passes
 * its localized fallback, so a value with nothing readable never surfaces as `String(value)`
 * ("[object Object]"); without one that string is a diagnostic and stays unmarked.
 */
export const toError = (error: unknown, fallback?: string): Error => {
  if (error instanceof Error) {
    return error
  }
  const text = getDisplayMessage(error) ?? fallback
  return text === undefined ? new Error(String(error)) : userFacing(new Error(text))
}

/**
 * A failure nobody raised on purpose and no server answered: an Error that carries neither
 * a response (a status or a body) nor the user-facing mark, and is not a transport failure.
 * In practice a code bug or a third-party script's own exception. It is shown as the generic
 * message, so it has to be reported to be seen at all.
 */
export const isUnexpectedError = (error: unknown): error is Error =>
  error instanceof Error &&
  !isUserFacing(error) &&
  !isTransportError(error) &&
  getErrorStatus(error) === undefined &&
  getApiErrorData(error) === undefined
